import {
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { ChangeEvent, FC, forwardRef, useContext, useState } from "react";
import {
  Controller,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { IMaskInput } from "react-imask";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { PPProps } from "./PPField";
import { cloneDeep, isEqual } from "lodash";
import { logger } from "../../utils/logger";

const fontColor: string = "text.secondary";
const fieldIsRequired: string = "Field is required";
const invalidZipFormat: string = "Invalid zipcode";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskZipCode = forwardRef<HTMLElement, CustomProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="`00000-0000"
      definitions={{
        "#": /[1-9]/,
      }}
      inputRef={ref as any}
      onAccept={(value: any) =>
        onChange({ target: { name: props.name, value: value } })
      }
      overwrite
    />
  );
});

TextMaskZipCode.displayName = "TextMaskZipCode";

interface State {
  textmask: string;
}

const ZipCode: FC<PPProps> = (props) => {
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];
  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const [errorMessage, setErrorMessage]: [
    string,
    React.Dispatch<React.SetStateAction<string>>
  ] = useState(fieldIsRequired);

  const [values, setValues] = useState<State>({
    textmask: "",
  });

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (appContext: AppContextType) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext,
          undefined, // sourceTranistionalState
          undefined, // lstContext
          undefined, // setLstContext
          undefined, // queryParams
          setAppContext // setAppContext
        );
      });
    }
  };

  return (
    <Controller
      render={({ field }) => {
        const isFieldInvalid: boolean =
          appContext.fieldState[field.name] !== undefined &&
          !appContext.fieldState[field.name];
        const isHideField: boolean =
          appContext.fieldHide[field.name] !== undefined &&
          appContext.fieldHide[field.name];
        const isDisabledField: boolean =
          appContext.fieldDisable[field.name] !== undefined &&
          appContext.fieldDisable[field.name];
        return (
          <FormControl error={isFieldInvalid} fullWidth>
            <TextField
              {...field}
              sx={isHideField ? { display: "none" } : null}
              disabled={isDisabledField}
              InputProps={{
                name: "textmask",
                inputComponent: TextMaskZipCode as any,
                inputProps: {
                  component: IMaskInput,
                },
                value: field.value,
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  // Get input value and update form
                  const value = event.target.value;
                  field.onChange(value);
                  
                  // Validate ZIP format
                  const zipRegEx = /^\d{5}(?:[-\s]\d{4})?$/;
                  let newAppContext: AppContextType = cloneDeep(appContext);
                  const isValidFormat: boolean =
                    value === "" ||
                    value === undefined ||
                    zipRegEx.test(value);
                  
                  // Update field validation state
                  newAppContext.fieldState[field.name] =
                    (isRequired &&
                      value?.length > 0 &&
                      isValidFormat) ||
                    (!isRequired && isValidFormat);
                  
                  // Update error message
                  setErrorMessage(
                    isValidFormat ? fieldIsRequired : invalidZipFormat
                  );
                  
                  // Always call onChange handlers for any ZIP change - 
                  // this ensures filterComboBoxByCountyFIPS is called consistently
                  onChange(newAppContext);
                  
                  // Update context if changed
                  if (!isEqual(appContext, newAppContext))
                    setAppContext(newAppContext);
                  
                  logger.debug(`ZIP changed to "${value}" in field ${field.name}`);
                  
                  return value;
                },
              }}
              variant="outlined"
              id={props.id}
              label={
                <Typography
                  sx={{ color: isFieldInvalid ? red.A700 : fontColor }}
                >
                  {props.label + (isRequired ? " *" : "")}
                </Typography>
              }
              fullWidth
            />
            <FormHelperText sx={isHideField ? { display: "none" } : null}>
              {isFieldInvalid ? (
                errorMessage
              ) : (
                <HelperText helperText={props.helperText}></HelperText>
              )}
            </FormHelperText>
          </FormControl>
        );
      }}
      name={props.id}
      control={control}
    />
  );
};

export default ZipCode;
